import React, { ChangeEvent, useEffect, useState } from 'react';

import AuthService from '../services/AuthService';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import CountdownTimer from './CountdownTimer';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { EmailRegexp } from '../constants';
import EmailUpdateFail from '../../membership/components/EmailUpdateFail';
import EmailUpdateSuccess from '../../membership/components/EmailUpdateSuccess';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorPanel from '../../shared/components/ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import RequestVerificationEmailFail from '../../membership/components/RequestVerificationEmailFail';
import RequestVerificationEmailSuccess from '../../membership/components/RequestVerificationEmailSuccess';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useEmailUpdates from '../hooks/useEmailUpdates';
import useSiteSettings from '../../siteadmin/hooks/useSiteSettings';

interface ChangeEmailDialogProps {
  userkey: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  open: boolean;
  forAdmin: boolean;
  unlink?: boolean;
  unlinkSuccess: boolean;
  onClose: (email?: string) => void;
  onUnlink: () => void;
}

const adminWarning =
  'The user must be logged out before you change their email address. If they are not, they may experience difficulty accessing the site.';
const unlinkWarning =
  'You must enter a valid email address to confirm unlinking.';
const userWarning = 'Changing your email will log you out automatically';

function ChangeEmailDialog(props: ChangeEmailDialogProps) {
  const locationOrigin = window.location.origin;
  const tenant = AuthService.Tenant;
  const [email, setEmail] = useState<string>(props.email || '');
  const [emailOk, setEmailOk] = useState<boolean>(false);
  const [changed, setChanged] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const {
    checkEmail,
    checkEmailLoading,
    checkEmailError,
    checkEmailData,
    requestEmailUpdate,
    requestEmailUpdateLoading,
    requestEmailUpdateError,
    requestEmailUpdateData
  } = useEmailUpdates();

  const { loadingSiteSettings, siteSettingsError, siteSettingsData } =
    useSiteSettings();

  const requireEmailVerification =
    siteSettingsData?.siteSettings?.flags.requireEmailVerification;

  useEffect(() => {
    if (checkEmailData?.isUsernameAvailable === true) {
      setEmailOk(true);
    }
  }, [checkEmailData]);

  useEffect(() => {
    if (requestEmailUpdateData) {
      setEmailSent(true);
      if (props.unlink) {
        props.onUnlink();
      }
    }
  }, [requestEmailUpdateData]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (!changed) {
      setChanged(true);
    }
  };

  const canSubmit = () => {
    return (
      EmailRegexp.test(email) &&
      !!!siteSettingsError &&
      (emailOk ||
        (changed &&
          email.trim() &&
          email.trim() !== props.email &&
          !requestEmailUpdateLoading &&
          !requestEmailUpdateError))
    );
  };

  const errorMessage = () => {
    if (!emailOk && changed && !!!email.trim()) {
      return 'Email address is required.';
    }

    if (!emailOk && changed && checkEmailData?.isUsernameAvailable === false) {
      return 'This address is already in use by another account.';
    }

    if (checkEmailError) {
      return 'Checking new email availability has failed.';
    }
  };

  const close = () => {
    props.onClose();
  };

  const logout = () => {
    props.onClose();
    AuthService.logout();
    window.close();
  };

  const handleSentEmail = () => {
    props.onClose(requestEmailUpdateData ? email : '');
  };

  const verifyEmail = () => {
    requestEmailUpdate(
      locationOrigin,
      tenant,
      props.userkey,
      email,
      requireEmailVerification
    );
  };

  const handleOk = () => {
    if (emailOk) {
      verifyEmail();
    } else {
      if (email.trim()) {
        checkEmail(tenant, email);
      }
    }
  };

  return (
    <Dialog open={props.open} fullWidth maxWidth="xs">
      <DialogTitle>
        {props.forAdmin
          ? `Update email address for '${props.username}'.`
          : 'Update my email address'}
      </DialogTitle>
      <DialogContent>
        {loadingSiteSettings && 'Loading...'}
        {siteSettingsError ? (
          <ErrorPanel
            title="Whoops!"
            message="Loading the site settings has failed."
          />
        ) : (
          siteSettingsData &&
          requestEmailUpdateData === undefined &&
          !requestEmailUpdateError && (
            <Grid container direction="column" spacing={1}>
              {
                <Grid container wrap="nowrap" alignItems="center" spacing={1}>
                  <Grid minWidth={'unset'}>
                    <ErrorIcon style={{ color: '#ef6c00' }} />
                  </Grid>
                  <Grid>
                    <Typography variant="body2">
                      {!props.forAdmin
                        ? props.unlink
                          ? requireEmailVerification
                            ? `${unlinkWarning}
                           You will then be logged out automatically and sent a verification email.`
                            : `${unlinkWarning}`
                          : requireEmailVerification
                          ? `${userWarning} and send a verification email to log back in with.`
                          : `${userWarning}`
                        : adminWarning}
                    </Typography>
                  </Grid>
                </Grid>
              }
              <Grid>
                <TextField
                  label="Email"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  fullWidth
                  autoFocus
                  value={email}
                  error={!!errorMessage()}
                  helperText={errorMessage()}
                  disabled={checkEmailLoading || emailOk}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {emailOk && <CheckIcon style={{ color: 'green' }} />}
                      </InputAdornment>
                    )
                  }}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          )
        )}
        {requestEmailUpdateData &&
          (requireEmailVerification ? (
            <RequestVerificationEmailSuccess email={email} />
          ) : (
            <EmailUpdateSuccess
              email={email}
              admin={props.forAdmin}
              unlinkMessage={
                props.unlinkSuccess
                  ? ' and your account has been successfully unlinked'
                  : ''
              }
            />
          ))}
        {requestEmailUpdateError &&
          (requireEmailVerification ? (
            <RequestVerificationEmailFail />
          ) : (
            <EmailUpdateFail />
          ))}
      </DialogContent>
      <DialogActions>
        {emailSent ? (
          props.forAdmin ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSentEmail}
            >
              Close
            </Button>
          ) : (
            <CountdownTimer
              message="Logging out in {{time}} seconds..."
              timeout={10}
              onTimeout={logout}
            />
          )
        ) : (
          <>
            <Button variant="outlined" color="primary" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!canSubmit()}
              onClick={handleOk}
            >
              {emailOk
                ? requestEmailUpdateLoading
                  ? 'Updating...'
                  : requireEmailVerification
                  ? 'Send Email'
                  : 'Update'
                : checkEmailLoading
                ? 'Checking...'
                : 'Check availability'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ChangeEmailDialog;
